import { gateLayout } from './layout.js';
import { CONFIG, STATION_FOOTPRINTS } from './config.js';
import { activeLayout, activeAirport } from './active-layout.js';
import { queuePoint as placedQueuePoint, servicePoint as placedServicePoint, stationPad as placedStationPad, at, type Pose } from '../content/placement.js';

/**
 * Where a gate's boarding desk stands and which way its line forms. A map gate's desk stands 140 inside its door,
 * facing into the terminal; the shared plan's gates repeat the Boarding desk along the top row.
 */
export function gatePose(airportId: string, gate: number): Pose {
    const row = gateLayout(airportId).GATES as { x: number; y: number; door?: { x: number; y: number }; facing?: number[] }[];
    const g = row[gate] ?? row[0];
    if (g.door && g.facing) { const [fx, fy] = g.facing; return { x: g.door.x - fx * 140, y: g.door.y - fy * 140, facing: [-fx || 0, -fy || 0] }; }
    const s = CONFIG.stages[6];
    return { x: s.x + (g.x - row[0].x), y: s.y, facing: s.facing ?? [0, 1] };
}
/** Desks with gate 6 swapped for one gate's pose, cached per active floor plan. */
const cache = new Map<string, Pose[]>();
function stagesFor(airportId: string, gate: number): Pose[] {
    const key = `${activeLayout()}|${airportId}|${gate}`;
    let stages = cache.get(key);
    if (!stages) {
        if (cache.size > 64) cache.clear();
        stages = CONFIG.stages.map((s, i) => i === 6 ? gatePose(airportId, gate) : s);
        cache.set(key, stages);
    }
    return stages;
}
/** Forget cached gate desks (the active floor plan changed). */
export function clearGateCache() { cache.clear(); }

export function queuePoint(index: number, rank: number, gate = 0, airportId = activeAirport()) {
    return placedQueuePoint(index === 6 ? stagesFor(airportId, gate) : CONFIG.stages, STATION_FOOTPRINTS, index, rank);
}
export function servicePoint(index: number, slot: number, lanes = 1, gate = 0, airportId = activeAirport()) {
    return placedServicePoint(index === 6 ? stagesFor(airportId, gate) : CONFIG.stages, STATION_FOOTPRINTS, index, slot, lanes);
}

/** Where a boarding passenger with a question waits: beside the desk's service point. */
export function inquiryPoint(gate = 0, airportId = activeAirport()) {
    const pose = gatePose(airportId, gate);
    return at(pose, 48, STATION_FOOTPRINTS[6].h / 2 + 32);
}

/** Old top-row x distance between a gate and the first gate (shared plan only; map gates use gatePose). */
export function gateOffset(airportId: string, gate: number): number {
    const row = gateLayout(airportId).GATES;
    return (row[gate] ?? row[0]).x - row[0].x;
}
export function boardingWorkPoint(gate = 0, airportId = activeAirport()) {
    return placedStationPad(stagesFor(airportId, gate), STATION_FOOTPRINTS, 6, 'work');
}
